import { useCallback, useEffect, useRef, useState } from "react"
import type { OrgUser } from "./types"

const USER_SEARCH_DEBOUNCE_MS = 300

export function useUserSearch(searchUsers: (query: string) => Promise<OrgUser[]>) {
  const [query, setQueryState] = useState("")
  const [results, setResults] = useState<OrgUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const searchRequestIdRef = useRef(0)

  const setQuery = useCallback((nextQuery: string) => {
    searchRequestIdRef.current += 1
    setError(null)
    setQueryState(nextQuery)
  }, [])

  const runSearch = useCallback(async (searchTerm: string) => {
    const requestId = ++searchRequestIdRef.current
    setError(null)
    setLoading(true)
    try {
      const userResponse = await searchUsers(searchTerm)
      if (requestId !== searchRequestIdRef.current) return
      setResults(userResponse)
    } catch (err) {
      if (requestId !== searchRequestIdRef.current) return
      setError((err as Error)?.message || "Failed to fetch organization users")
    } finally {
      if (requestId === searchRequestIdRef.current) setLoading(false)
    }
  }, [searchUsers])

  useEffect(() => {
    if (!query.trim()) {
      setLoading(false)
      setResults([])
      return
    }
    const debounceTimer = setTimeout(() => runSearch(query), USER_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(debounceTimer)
  }, [query, runSearch])

  return { query, setQuery, results, loading, error }
}
